<script>
import MarketPercentage from './MarketPercentage.vue';
import RacingOddsComparisonTableOddCell from './RacingOddsComparisonTableOddCell.vue';
import SparkLine from '../SparkLine/SparkLine.vue';
import BetTypeSwitcher from './BetTypeSwitcher.vue';
import { convertBetType } from '../../utils/convert-bet-type.js';
import _ from 'lodash';
import HandleTableScroll from '../../mixins/HandleTableScroll.js';

export default {
  name: 'RacingOddsComparisonTableDesktop',
  components: {
    MarketPercentage,
    RacingOddsComparisonTableOddCell,
    BetTypeSwitcher,
    SparkLine,
  },
  mixins: [HandleTableScroll],
  props: {
    tableData: {
      type: Object,
      required: true,
    },
    odds: {
      type: Object,
      required: true,
    },
    eventId: {
      // eslint-disable-line
      type: [String, Number],
      default: null,
    },
    betType: {
      type: String,
      default: () => null,
    },
  },
  data() {
    return {
      tableInDefaultPosition: true,
      showIndicator: false,
      showFullTable: false,
      quickbetRef: {},
      coreQuickbetBookmakers: '',
    };
  },
  computed: {
    highestOddsList() {
      const { tableData, odds } = this;
      const list = {};
      for (let i = 0, l = tableData.selections.length; i < l; i++) {
        const selection = tableData.selections[i];
        const conditions = {
          selectionId: parseInt(selection.id),
          betType: 'fixed-win',
        };
        const row = _.filter(odds, conditions);
        const highestOdds = _.maxBy(row, odds => {
          return odds.price.value;
        });

        if (highestOdds) {
          list[selection.id] = highestOdds.price.value;
        }
      }
      return list;
    },
    sortedSelections() {
      const { odds } = this;
      const { selections } = this.tableData;

      const combinedSelectionsOdds = [];
      let pricesWithOdds = [];
      for (const selection of selections) {
        const key = `${selection.id}-best-fixed-win`;

        // add the odds to each selection
        if (selection.prices.length > 0) {
          pricesWithOdds = selection.prices.map(price => {
            const priceOdds = odds[`${selection.id}-${price.bookmakerLower}-${price.betType}`];
            const oddsValue = priceOdds ? `${priceOdds.price.value}` : '';
            const line = priceOdds && priceOdds.hasOwnProperty('line') ? priceOdds.line.value : null;

            return {
              ...price,
              line,
              odds: oddsValue,
            };
          });
        }

        // not sure if there's an explicit need to check for undefined but didn't want to change the logic
        const price = odds[key] !== undefined ? odds[key].price.value : undefined;
        const selectionWithOdds = { ...selection, prices: pricesWithOdds };
        combinedSelectionsOdds.push({
          selection: selectionWithOdds,
          price,
        });
      }

      if (combinedSelectionsOdds.length > 10) {
        return this.showFullTable ? combinedSelectionsOdds : combinedSelectionsOdds.slice(0, 10);
      } else {
        this.showFullTable = true; // eslint-disable-line vue/no-side-effects-in-computed-properties
        return combinedSelectionsOdds;
      }
    },
    selectionIds() {
      const oddsTableData = this.$store.getters['RacingOddsComparisonTable/getData'];
      if (oddsTableData.hasOwnProperty('events') && oddsTableData.events.length > 0) {
        const indexOfLastItem = oddsTableData.events.length - 1;
        const { selections } = oddsTableData.events[indexOfLastItem];
        return selections.map(selection => parseInt(selection.id));
      }
    },
  },
  beforeMount() {
    // Retrieve odds for this specific table
    const { betType, toggleIndicator } = this;
    if (!betType) return;
    // To decide whether show indicator bar or not
    toggleIndicator();
    // Run function while window resize
    window.addEventListener('resize', function() {
      toggleIndicator();
      this.tableInDefaultPosition = false;
    });
  },
  methods: {
    getQuickbetConfig: function(selectionData, selectionOdds) {
      const { selection } = selectionData;
      const selectionTitle = `${selection.competitorNumber}. ${selection.name} (${selection.barrierNumber})`;
      const selectionSubTitle = `${this.tableData.eventName}`;
      const imageUrl = selection.imageUrl;

      return {
        selectionTitle,
        selectionSubTitle,
        imageUrl,
        bookmakerId: selectionOdds.bookmakerLower,
        selectionId: parseInt(selectionData.selection.id),
        eventId: this.eventId,
        betType: selectionOdds.betType,
      };
    },
    tableScroll: function(e) {
      const tableRight = e.srcElement.previousElementSibling;
      // call method which is declared in mixins `HandleTableScroll`
      this.handleTableScroll(tableRight);
    },
    toggleIndicator: function() {
      const windowWidth = document.documentElement.clientWidth;
      if (windowWidth <= 760 || this.$refs.octdRight === undefined) return;

      const tableRightWidth = this.$refs.octdRight.clientWidth;
      const scrollWidth = this.$refs.octdRight.scrollWidth;
      if (scrollWidth <= tableRightWidth) {
        if (tableRightWidth > 0) {
          this.showIndicator = false;
        } else {
          // In this condition,
          // the closed accordion needs Scrolling Indicator attached,
          if (windowWidth < 1322) {
            this.showIndicator = true;
          }
        }
      } else {
        this.showIndicator = true;
      }
    },
    convertBetType: betType => convertBetType(betType),
    enableLegacyQuickbet: function(bookmakerId) {
      return !this.coreQuickbetBookmakers.includes(bookmakerId);
    },
    // handles Firefox opening new tab on quickbet launch
    getLinkInfo(selection, type) {
      const { enableLegacyQuickbet } = this;
      const { bookmakerLower, betUrl } = selection;
      if (type === 'url') {
        return enableLegacyQuickbet(bookmakerLower) ? betUrl : 'javascript:void(-1)';
      }
      if (type === 'target') {
        return enableLegacyQuickbet(bookmakerLower) ? '_blank' : '';
      }
    },
    updateBookmakerBalance(data) {
      const { bookmaker, balance } = data;
      this.$store.dispatch('Account/updateBookmakerBalance', { bookmaker, balance });
    },
  },
};
</script>

<template>
  <div>
    <div
      :class="{ 'table-needs-to-scroll-left': tableInDefaultPosition !== true }"
      class="octd-wrap"
    >
      <!-- Left -->
      <div class="octd-left octd-left--racing">
        <div class="octd-left__header">
          <BetTypeSwitcher :event-id="eventId || tableData.eventId" />
        </div>
        <div class="octd-left__main">
          <div
            v-for="(selectionData, index) in sortedSelections"
            :key="index"
            :class="{'octd-left__main-row--truncate': selectionData.selection.competitor.name.length > 40 }"
            class="octd-left__main-row main-row--racing"
          >
            <div class="octd-left__main-row-racing">
              <div class="octd-left__racing-details">
                <span
                  v-if="selectionData.selection.silk_image_url_small"
                  :class="[{'silk-wrap--resulted': selectionData.selection.result}]"
                  class="octd-left__silk-wrap"
                >
                  <span
                    v-if="selectionData.selection.result && selectionData.selection.result < 4"
                    :class="{'first-place': selectionData.selection.result < 2}"
                    class="octd-left__result"
                  >{{ selectionData.selection.result | numberOrdinal }}</span>
                  <img
                    v-else
                    :src="selectionData.selection.silk_image_url_small"
                    :alt="selectionData.selection.competitor.name"
                    class="octd-left__silk"
                  >
                </span>
                <span class="competitor-details">
                  <a :href="selectionData.selection.profileUrl" target="_blank">
                    <strong>{{ selectionData.selection.competitor_number }}</strong>
                    {{ selectionData.selection.competitor.name }}
                    <span
                      v-if="selectionData.selection.barrier_number"
                    >({{ selectionData.selection.barrier_number }})</span>
                  </a>
                  <span
                    v-if="selectionData.selection.jockey.short_name"
                    class="octd-left__jockey-details"
                  >
                    <a :href="selectionData.selection.jockeyUrl">
                      <strong>J</strong>
                      : {{ selectionData.selection.jockey.short_name }}
                    </a>
                    <span class="octd-left__weight">{{ selectionData.selection.weight }}</span>
                  </span>
                </span>
              </div>
              <SparkLine
                v-if="selectionData.selection.flucs"
                :points="JSON.parse(selectionData.selection.flucs)"
                class="octd-left__spark-line"
              />
            </div>
          </div>
        </div>
        <div class="octd-left__footer">
          <div class="octd-left__footer-row">Market%</div>
        </div>
      </div>

      <!-- Right -->
      <div ref="octdRight" class="octd-right">
        <div class="octd-right__header">
          <div
            v-for="(bookie, index) in tableData.agencies"
            :key="index"
            :style="'background-image:url(' + bookie.oddsGridLogoBackground + ')'"
            class="octd-right__header-cell"
          >
            <a :href="bookie.claimUrl" rel="nofollow" target="_blank">
              <img :src="bookie.oddsGridLogo" class="octd-right__bookie-logo" >
            </a>
          </div>
          <div v-show="showIndicator" class="octd-right__header-cushion"></div>
        </div>
        <div class="octd-right__main">
          <div
            v-for="(selectionData, index1) in sortedSelections"
            :key="index1"
            class="octd-right__main-row"
          >
            <div
              v-for="(selectionOdds, index2) in selectionData.selection.prices"
              :key="index2"
              :class="selectionOdds.bookmakerLower + '-line' + ' ' + selectionOdds.bookmakerLower + '-' + selectionData.selection.id"
              :ref="selectionOdds.bookmakerLower + '-' + selectionData.selection.id"
              class="octd-right__main-cell"
            >
              <a
                v-if="selectionOdds.odds"
                :class="{'quickbet-betslip': enableLegacyQuickbet(selectionOdds.bookmakerLower) && selectionOdds.odds.length > 0 && selectionOdds.quickbetEnabled }"
                :data-key="selectionOdds.oddsKey"
                :href="getLinkInfo(selectionOdds, 'url')"
                :target="getLinkInfo(selectionOdds, 'target')"
                class="octd-right__main-cell-link"
              >
                <RacingOddsComparisonTableOddCell
                  :selection-id="parseInt(selectionData.selection.id)"
                  :bookmaker-id="selectionOdds.bookmakerLower"
                  :odds="selectionOdds.odds"
                  :line="selectionOdds.line"
                  :bet-type="selectionOdds.betType"
                  :highest-odds-list="highestOddsList"
                  :quickbet-enabled="selectionOdds.quickbetEnabled"
                />
              </a>
            </div>
            <div class="octd-right__border-line"></div>
          </div>
        </div>
        <div class="octd-right__footer">
          <div
            v-for="(bookmaker, index) in tableData.agencies"
            :key="index"
            class="octd-right__footer-cell"
          >
            <MarketPercentage
              :bookmaker-id="bookmaker.id"
              :event-id="eventId"
              :bet-type="betType"
              :selection-ids="selectionIds"
              :is-racing-related="true"
            />
          </div>
        </div>
      </div>

      <!-- Scrolling indicator -->
      <div v-if="showIndicator === true" class="octd-scroll-indicator" @click="tableScroll"></div>

      <!-- show more button -->
      <div
        v-if="showFullTable === false"
        class="octd-show-more-btn"
        @click="showFullTable = true"
      >show more</div>
    </div>
  </div>
</template>
